/**
 * 🧑‍💻 SWE-2: 사용량 추적 훅
 * License Server의 /api/v1/usage 엔드포인트 연동
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';

// API URL (환경변수에서 가져오거나 기본값 사용)
const LICENSE_API_URL = import.meta.env.VITE_LICENSE_API_URL || 'https://moldclaw-license.jungwoo.workers.dev';

export interface UsageResource {
  used: number;
  limit: number;
  percentage: number;
}

export interface UsageData {
  tokens: UsageResource;
  requests: UsageResource;
  storage: UsageResource;
}

export interface UsagePeriod {
  start: string;
  end: string;
}

export interface UsageState {
  data: UsageData | null;
  period: UsagePeriod | null;
  isLoading: boolean;
  error: string | null;
}

export interface UseUsageReturn extends UsageState {
  refetch: () => Promise<void>;
  formatStorage: (bytes: number) => string;
  formatNumber: (num: number) => string;
}

/**
 * 사용량 데이터를 가져오고 관리하는 훅
 */
export function useUsage(): UseUsageReturn {
  const { session } = useAuth();
  const [state, setState] = useState<UsageState>({
    data: null,
    period: null,
    isLoading: true,
    error: null,
  });

  const fetchUsage = useCallback(async () => {
    if (!session?.access_token) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: null,
        data: getDefaultUsage(),
      }));
      return;
    }

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const response = await fetch(`${LICENSE_API_URL}/api/v1/usage`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        // 401인 경우 기본값 반환 (새 사용자)
        if (response.status === 401) {
          setState({
            data: getDefaultUsage(),
            period: getCurrentPeriod(),
            isLoading: false,
            error: null,
          });
          return;
        }
        throw new Error(`Failed to fetch usage: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        setState({
          data: result.data,
          period: result.period,
          isLoading: false,
          error: null,
        });
      } else {
        throw new Error(result.error?.message || 'Unknown error');
      }
    } catch (error) {
      console.error('Usage fetch error:', error);
      setState({
        data: getDefaultUsage(),
        period: getCurrentPeriod(),
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to load usage',
      });
    }
  }, [session?.access_token]);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  return {
    ...state,
    refetch: fetchUsage,
    formatStorage,
    formatNumber,
  };
}

/**
 * 기본 사용량 데이터 (새 사용자 또는 오류 시)
 */
function getDefaultUsage(): UsageData {
  return {
    tokens: { used: 0, limit: 10000, percentage: 0 },
    requests: { used: 0, limit: 100, percentage: 0 },
    storage: { used: 0, limit: 104857600, percentage: 0 }, // 100MB
  };
}

/**
 * 현재 기간 계산
 */
function getCurrentPeriod(): UsagePeriod {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
  
  return {
    start: start.toISOString(),
    end: end.toISOString(),
  };
}

/**
 * 바이트를 읽기 쉬운 형태로 변환
 */
export function formatStorage(bytes: number): string {
  if (bytes === 0) return '0 B';
  
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

/**
 * 숫자를 천 단위 구분 형태로 변환
 */
export function formatNumber(num: number): string {
  return num.toLocaleString('ko-KR');
}

/**
 * 한도 조회 훅
 */
export function useUsageLimits() {
  const [limits, setLimits] = useState<{
    free: { tokens: number; requests: number; storage: number };
    pro: { tokens: number; requests: number; storage: number };
    team: { tokens: number; requests: number; storage: number };
  } | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchLimits = async () => {
      try {
        const response = await fetch(`${LICENSE_API_URL}/api/v1/usage/limits`);
        const result = await response.json();
        
        if (result.success) {
          setLimits(result.data);
        }
      } catch (error) {
        console.error('Failed to fetch limits:', error);
        // 기본값 설정
        setLimits({
          free: { tokens: 10000, requests: 100, storage: 104857600 },
          pro: { tokens: 100000, requests: 1000, storage: 1073741824 },
          team: { tokens: 1000000, requests: 10000, storage: 10737418240 },
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchLimits();
  }, []);

  return { limits, isLoading };
}

export default useUsage;
